import { formatSalesOrderNumber } from '@zspace/format';
import { SalesOrder } from '@zspace/types';
import { Fragment, useMemo } from 'react';
import { Element } from 'react-bulma-components';
import Conditional from '../../shared/conditional/conditional';
import If from '../../shared/if/if';
import Link from '../../ui/link/link';

export type SalesOrdersListProps = {
  list: Pick<SalesOrder, 'id' | 'number'>[];
  textColor?: 'primary' | 'primary-dark';
  link?: string;
  hasMoreLink?: string;
  openLinksInNewTab?: boolean;
  maxElements?: number;
};

const MAX_ELEMENTS = 3;

export function SalesOrdersList({
  list,
  textColor = 'primary-dark',
  link,
  hasMoreLink: _hasMoreLink,
  openLinksInNewTab = false,
  maxElements = MAX_ELEMENTS,
}: SalesOrdersListProps) {
  const hasMore = useMemo(
    () => list.length > maxElements,
    [list.length, maxElements]
  );
  const hasMoreLink = useMemo(() => _hasMoreLink ?? link, [_hasMoreLink, link]);

  const textColorClassName = useMemo(
    () => `has-text-${textColor}`,
    [textColor]
  );

  if (list.length === 0) {
    return null;
  }

  return (
    <Element display="inline-flex" alignItems="center">
      <span>
        {list.slice(0, maxElements).map((so, index, { length }) => (
          <Fragment key={so.id}>
            <Link
              to={link || `/sales-orders/${so.id}`}
              underlined
              color={textColor}
              openNewTab={openLinksInNewTab}
            >
              {formatSalesOrderNumber(so.number)}
            </Link>
            <If condition={index < length - 1}>
              <span className={textColorClassName}>{', '}</span>
            </If>
          </Fragment>
        ))}
      </span>
      <If condition={hasMore}>
        <Conditional condition={!!hasMoreLink}>
          <Conditional.True>
            <span>
              <Link
                to={hasMoreLink!}
                className={'ml-1 is-underlined'}
                color={textColor}
                openNewTab={openLinksInNewTab}
              >
                and {list.length - maxElements} more
              </Link>
            </span>
          </Conditional.True>
          <Conditional.False>
            <span className={`ml-1 ${textColorClassName}`}>
              and {list.length - maxElements} more
            </span>
          </Conditional.False>
        </Conditional>
      </If>
    </Element>
  );
}

export default SalesOrdersList;
